import React, { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import type { Locale, SectionKey } from "../../shared/dto";
import { answerSortRank } from "../../shared/answer-catalog";
import { PartnerControl } from "../PartnerControl";
import { LanguagePreference } from "../LanguagePreference";
import { ActivityTimeline } from "../activity";
import { AnswersView } from "./AnswersView";
import { formatDate, makeSafeT, phaseLabel } from "./core";
import type { StaffPaneProps } from "./console-types";
import {
  NOTIFICATION_VISIBLE_MAX,
  groupNotifications,
  notificationShowsAttempts,
  notificationStatusLabel,
  notificationStatusTone,
  notificationTitle,
  notificationTypeHint,
  type NotificationMessage,
  type NotificationRowLike
} from "./notification-presentation";
import "./console-panels.css";
import { normalizePhone, partnerTelegramUrl } from "../../shared/partner-telegram";

/**
 * The client's own notification queue.
 *
 * 🔴 F138 kolečko 2. This pane used to draw `String(item.type)` and
 * `String(item.status)` straight into the card, so it printed
 * "deliverable_partner_review / sent" and "client_step_unlocked / failed" -
 * internal codes as a card heading. It survived because it lived at the bottom
 * of a tab inside a drawer; F138a lifted it onto the PRIMARY surface, where it
 * is the first thing an admin reads about a client's messages.
 *
 * Both halves now speak: the title through the shared presenter the Servis
 * queue uses (the subject a human would recognise, else the type's human name)
 * and the state through the state tokens, never a colour of its own.
 */
export function NotificationsPane(props: StaffPaneProps) {
  const rows = (props.detail.notifications ?? []) as unknown as NotificationRowLike[];
  if (!rows.length) return <p className="staff-empty">{props.t("staffConsole.emptyNotifications")}</p>;
  const messages = groupNotifications(rows);
  const visible = messages.slice(0, NOTIFICATION_VISIBLE_MAX);
  const older = messages.slice(NOTIFICATION_VISIBLE_MAX);
  const card = (message: NotificationMessage) => (
    <NotificationCard key={message.key} message={message} {...props} />
  );
  return (
    <div className="client-notification-list">
      <div className="staff-card-list">{visible.map(card)}</div>
      {/* 🔴 Folding is the HISTORY's own idiom one card over ("Starší aktivita
          (65)"), and the history had it while the queue did not - which is the
          entire reason one column of this section was ten thousand pixels of
          wall beside a column that knew how to end. Same shape, same wording,
          and the fold sits at depth 0: it holds cards, it is never inside
          one, so the surface stays plocha -> sekce (směr, princip 2). */}
      {older.length > 0 && (
        <details className="client-notification-older">
          <summary>
            {props.t("staffConsole.clientNotifications.older").replace("{count}", String(older.length))}
          </summary>
          <div className="staff-card-list">{older.map(card)}</div>
        </details>
      )}
    </div>
  );
}

/**
 * ONE card per message. The channels it went out on are quiet chips carrying
 * their own state, the people it went to are named, and the internal strings
 * (the type nobody named, an id nobody could resolve) stay REACHABLE without
 * being the label.
 */
function NotificationCard(props: StaffPaneProps & { message: NotificationMessage }) {
  const { message } = props;
  const recipients = message.recipients.length
    ? message.recipients.join(" · ")
    : props.t("staffConsole.clientNotifications.recipientUnknown");
  return (
    <article className="staff-card client-notification-card" data-notification-type={message.type}>
      <div className="client-notification-head">
        <strong title={notificationTypeHint(message.type)}>
          {notificationTitle({ subject: message.subject, type: message.type }, props.t)}
        </strong>
        {message.createdAt && (
          <time className="client-notification-when" dateTime={message.createdAt}>
            {formatDate(message.createdAt, props.locale)}
          </time>
        )}
      </div>
      <p className="client-notification-recipient" title={message.unresolvedRecipients.join(" · ") || undefined}>
        <span className="client-notification-to">{props.t("staffConsole.clientNotifications.to")}</span>
        {recipients}
      </p>
      <ul className="client-notification-channels">
        {message.deliveries.map((delivery) => (
          <li key={delivery.channel}>
            {props.t(`staffConsole.clientNotifications.channel.${delivery.channel}`)}
            <span className={`client-notification-state is-${notificationStatusTone(delivery.status)}`}>
              {notificationStatusLabel(delivery.status, props.t)}
            </span>
          </li>
        ))}
      </ul>
      {notificationShowsAttempts(message) && (
        <small className="client-notification-attempts">
          {props.t("staffConsole.attempts")}: {message.attempts}
        </small>
      )}
      {/* 🔴 The action gets its own ROW. As a bare sibling it was an
          inline-block 44 px control sharing one line box with a 13 px note,
          starting at exactly the pixel the note ended (measured: gap 0 px at
          1440), so "Pokusy: 1" read as sitting underneath the button. The
          rule lives in this area's own sheet, never in staff.css, which
          another worker owns this shift. */}
      {message.failedRows.length > 0 && (
        <div className="client-notification-action">
          <button className="secondary-button compact-button" type="button" onClick={() => {
            // A message that failed on two addresses needs both retried; the
            // endpoint is per ROW, because a row is what carries the attempt.
            for (const row of message.failedRows) {
              void props.run(`/api/admin/notifications/${String(row.id)}/retry`, { method: "POST", body: {} });
            }
          }}>{props.t("staffConsole.retryNotification")}</button>
        </div>
      )}
    </article>
  );
}

// One pane must never blank the whole console: catch, show, allow retry.
export class PaneBoundary extends React.Component<{ label: string; retry: string; children?: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) {
      return (
        <div className="pane-error" role="alert">
          <p>{this.props.label}</p>
          <button className="secondary-button compact-button" type="button" onClick={() => this.setState({ failed: false })}>
            {this.props.retry}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Legacy section controls stay wired to the state machine, but they no longer
// pretend to be the numbered client journey.
export function phaseTitle(key: SectionKey, t: (key: string) => string): string {
  return t(`staffConsole.phaseName.${key}`);
}

// ---- Partner profile self-service (Pavel 2026-07-21) ----
// The partner maintains the exact card their clients see; the preview below
// the form renders that card live ("takhle se zobrazuje tvým klientům").
export function MyProfilePanel(props: {
  csrfToken: string;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  t: (key: string) => string;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    // F146: no `bio`. The client card prints a fixed sentence about what a
    // partner is for, so there is nothing here for the partner to write.
    displayName: "", photoUrl: "", calendlyUrl: "", publicEmail: "",
    telegramUsername: "", telegramId: "", googleAccount: "", phone: ""
  });
  const [state, setState] = useState<"loading" | "idle" | "saving" | "saved" | "error">("loading");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [photoName, setPhotoName] = useState("");
  // The photo the server already holds. A stored value the partner did not
  // touch is never re-validated (F61): older rows carry formats this form
  // would refuse, and refusing them blocked every OTHER field too.
  const loadedPhotoRef = useRef("");
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  // Opening "Můj profil" from deep inside a client detail must have a visible
  // result: close the account menu, bring the panel into the viewport and
  // take focus; on close, give both back (audit a3-06).
  const panelRef = useRef<HTMLElement | null>(null);
  const restoreRef = useRef<{ scrollY: number; element: HTMLElement | null }>({ scrollY: 0, element: null });
  useEffect(() => {
    restoreRef.current = {
      scrollY: window.scrollY,
      element: document.activeElement instanceof HTMLElement ? document.activeElement : null
    };
    for (const menu of document.querySelectorAll<HTMLDetailsElement>("details.staff-avatar-menu[open]")) {
      menu.removeAttribute("open");
    }
    const frame = window.requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ block: "start" });
      panelRef.current?.focus();
    });
    const restore = restoreRef.current;
    return () => {
      window.cancelAnimationFrame(frame);
      window.scrollTo({ top: restore.scrollY });
      if (restore.element?.isConnected) restore.element.focus();
    };
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/staff/profile", { credentials: "include" });
        const body = await response.json() as { ok: boolean; profile: Record<string, string | null> };
        if (!response.ok || !body.ok) throw new Error("profile_load_failed");
        loadedPhotoRef.current = body.profile.photoUrl ?? "";
        setForm({
          displayName: body.profile.displayName ?? "",
          photoUrl: body.profile.photoUrl ?? "",
          calendlyUrl: body.profile.calendlyUrl ?? "",
          publicEmail: body.profile.publicEmail ?? "",
          telegramUsername: body.profile.telegramUsername ?? "",
          telegramId: body.profile.telegramId ?? "",
          googleAccount: body.profile.googleAccount ?? "",
          phone: body.profile.phone ?? ""
        });
        setState("idle");
      } catch { setState("error"); }
    })();
  }, []);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
    setState((current) => current === "saved" ? "idle" : current);
  }

  // Photo upload: downscale client-side to a small JPEG data URI so it fits
  // the profile row and loads instantly in every client dashboard.
  function onPhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPhotoName(file.name);
    const url = URL.createObjectURL(file);
    const image = new Image();
    // 🔴 Every path out of here must SAY something (F61). The handler used to
    // listen for onload alone, so a file the browser cannot decode - a HEIC
    // straight from a phone is the common one - left the form untouched and
    // silent: the partner picked a photo and read that as "the button does
    // nothing". Measured in artifacts/fix-partnerux/photo-button-probe2.
    const fail = (messageKey: string) => {
      URL.revokeObjectURL(url);
      setPhotoName("");
      setFieldError(props.t(messageKey));
    };
    image.onerror = () => fail("staffConsole.myProfile.photoUnreadable");
    image.onload = () => {
      const size = 384;
      if (image.width < size || image.height < size) {
        fail("staffConsole.myProfile.photoTooSmall");
        return;
      }
      const canvas = document.createElement("canvas");
      const scale = Math.max(size / image.width, size / image.height);
      canvas.width = size; canvas.height = size;
      const context = canvas.getContext("2d");
      if (!context) {
        fail("staffConsole.myProfile.photoUnreadable");
        return;
      }
      setFieldError(null);
      context.drawImage(image, (size - image.width * scale) / 2, (size - image.height * scale) / 2,
        image.width * scale, image.height * scale);
      set("photoUrl", canvas.toDataURL("image/jpeg", 0.85));
      URL.revokeObjectURL(url);
    };
    image.src = url;
  }

  // Specific message instead of a generic "something broke" (Pavel 2026-07-24).
  function firstProblem(): string | null {
    if (form.displayName.trim().length < 2) return props.t("staffConsole.myProfile.vName");
    // Only a photo the partner actually changed is held to the format rules;
    // the stored one is the server's business (F61).
    if (form.photoUrl && form.photoUrl !== loadedPhotoRef.current
      && !/^https:\/\/\S+$/.test(form.photoUrl) && !/^data:image\//.test(form.photoUrl)) {
      return props.t("staffConsole.myProfile.vPhoto");
    }
    if (form.calendlyUrl && !/^https:\/\/\S+$/.test(form.calendlyUrl)) return props.t("staffConsole.myProfile.vCalendly");
    if (form.publicEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.publicEmail)) return props.t("staffConsole.myProfile.vEmail");
    if (form.telegramUsername && !/^@?[A-Za-z][A-Za-z0-9_]{4,31}$/.test(form.telegramUsername.trim())) return props.t("staffConsole.myProfile.vTelegram");
    if (form.telegramId && !/^\d{5,15}$/.test(form.telegramId.trim())) return props.t("staffConsole.myProfile.vTelegramId");
    if (form.phone && !normalizePhone(form.phone)) return props.t("staffConsole.myProfile.vPhone");
    if (form.googleAccount && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.googleAccount.trim())) return props.t("staffConsole.myProfile.vGoogle");
    return null;
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const problem = firstProblem();
    setFieldError(problem);
    if (problem) { setState("idle"); return; }
    setState("saving");
    try {
      const response = await fetch("/api/staff/profile", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
        body: JSON.stringify(form)
      });
      if (!response.ok) throw new Error("profile_save_failed");
      // What we just stored becomes the new "untouched" baseline.
      loadedPhotoRef.current = form.photoUrl;
      setState("saved");
    } catch { setState("error"); }
  }

  const preview = {
    userId: "preview", displayName: form.displayName || null, photoUrl: form.photoUrl,
    calendlyUrl: form.calendlyUrl || "#", publicEmail: form.publicEmail,
    telegramUrl: partnerTelegramUrl(form.telegramUsername, form.phone)
  };
  return (
    <section className="staff-my-profile" aria-labelledby="my-profile-title" ref={panelRef} tabIndex={-1}>
      <div className="staff-section-heading">
        <div>
          <p className="panel-label">{props.t("staffConsole.myProfile.kicker")}</p>
          <h2 id="my-profile-title">{props.t("staffConsole.myProfile.title")}</h2>
        </div>
        <button className="text-button" type="button" onClick={props.onClose}>{props.t("common.close")}</button>
      </div>
      {/* The console's own language, ABOVE the loading gate on purpose: it is
          not part of the profile form (it saves itself on click, never with
          the Save button), and a staff member whose /api/staff/profile call is
          slow or broken must still be able to leave a language they do not
          read. */}
      <div className="my-profile-language">
        <LanguagePreference
          locale={props.locale}
          csrfToken={props.csrfToken}
          t={props.t}
          onChanged={props.onLocaleChange}
          titleKey="staffConsole.myProfile.language.title"
          copyKey="staffConsole.myProfile.language.copy"
        />
      </div>
      {state === "loading" ? <p>{props.t("common.loading")}</p> : (
        <div className="my-profile-grid">
          <form className="my-profile-form" onSubmit={save}>
            <label>{props.t("staffConsole.myProfile.name")}
              <input type="text" value={form.displayName} onChange={(e) => set("displayName", e.target.value)} required minLength={2} maxLength={80} />
            </label>
            <div className="my-profile-photo-field">
              <span className="my-profile-photo-label" id="my-profile-photo-label">
                {props.t("staffConsole.myProfile.photo")}
              </span>
              <span className="my-profile-photo-control">
                {/* The form must tell the truth about the SAVED photo: with
                    one stored, "Zatím není vybraná žádná fotografie" only
                    described the empty file input and pushed the partner to
                    a pointless re-upload (audit a2-08). */}
                {form.photoUrl && !photoName && (
                  <img alt="" className="my-profile-photo-thumb" src={form.photoUrl} />
                )}
                {/* A real button, not a span dressed as one (F61): the old
                    markup took no focus and answered no Enter, so keyboard
                    users had no way to change their photo at all. */}
                <button
                  type="button"
                  className="secondary-button compact-button"
                  aria-describedby="my-profile-photo-label"
                  onClick={() => photoInputRef.current?.click()}
                >
                  {props.t(form.photoUrl ? "staffConsole.myProfile.changePhoto" : "staffConsole.myProfile.choosePhoto")}
                </button>
                <span>
                  {photoName
                    || props.t(form.photoUrl
                      ? "staffConsole.myProfile.photoSaved"
                      : "staffConsole.myProfile.noPhotoChosen")}
                </span>
                <input
                  ref={photoInputRef}
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp"
                  aria-label={props.t("staffConsole.myProfile.photo")}
                  onChange={onPhoto}
                />
              </span>
            </div>
            <ProfileCopyField
              label={props.t("staffConsole.myProfile.calendly")}
              onChange={(value) => set("calendlyUrl", value)}
              placeholder={props.t("staffConsole.myProfile.calendlyPlaceholder")}
              t={props.t}
              type="url"
              value={form.calendlyUrl}
            />
            <ProfileCopyField
              label={props.t("staffConsole.myProfile.email")}
              onChange={(value) => set("publicEmail", value)}
              required
              t={props.t}
              type="email"
              value={form.publicEmail}
            />
            <small className="my-profile-hint">{props.t("staffConsole.myProfile.emailHint")}</small>
            <label>{props.t("staffConsole.myProfile.google")}
              <input type="email" value={form.googleAccount} onChange={(e) => set("googleAccount", e.target.value)} />
            </label>
            <small className="my-profile-hint">{props.t("staffConsole.myProfile.googleHint")}</small>
            <label>{props.t("staffConsole.myProfile.telegram")}
              <input type="text" value={form.telegramUsername} onChange={(e) => set("telegramUsername", e.target.value)} placeholder={props.t("transfer.usernamePlaceholder")} />
            </label>
            <label>{props.t("staffConsole.myProfile.telegramId")}
              <input type="text" inputMode="numeric" value={form.telegramId} onChange={(e) => set("telegramId", e.target.value)} placeholder={props.t("staffConsole.myProfile.telegramIdPlaceholder")} />
            </label>
            <small className="my-profile-hint">{props.t("staffConsole.myProfile.telegramHint")}</small>
            <label>{props.t("staffConsole.myProfile.phone")}
              <input type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={props.t("staffConsole.myProfile.phonePlaceholder")} />
            </label>
            <small className="my-profile-hint">{props.t("staffConsole.myProfile.phoneHint")}</small>
            <div className="my-profile-actions">
              <button className="primary-button compact-button" type="submit" disabled={state === "saving"}>
                {props.t(state === "saving" ? "staffConsole.myProfile.saving" : "staffConsole.myProfile.save")}
              </button>
              {state === "saved" && <span className="message success-message">{props.t("staffConsole.myProfile.saved")}</span>}
              {fieldError && <span className="message error-message">{fieldError}</span>}
              {state === "error" && !fieldError && <span className="message error-message">{props.t("common.error")}</span>}
            </div>
          </form>
          <div className="my-profile-preview">
            <p className="panel-label">{props.t("staffConsole.myProfile.previewLabel")}</p>
            <p className="eyebrow blue-eyebrow">{props.t("partner.eyebrow")}</p>
            <PartnerControl partner={preview} t={props.t} />
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * A profile field whose value can be long: Calendly and the client-facing
 * address.
 *
 * The old shape printed the stored value a SECOND time under the input
 * whenever it was non-empty - so a seven-field form carried two muted blocks
 * that read like error messages under two of the fields (audit P-09; the
 * inputs were measured as NOT truncated). The duplicate exists
 * to show what the input CUT OFF, so it renders only when the input really
 * cuts something off, and the copy control moved INTO the field where it
 * belongs to the value rather than to the duplicate.
 */
export function ProfileCopyField(props: {
  label: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  t: (key: string) => string;
  type: "url" | "email";
  value: string;
}) {
  const fieldId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    // Truncation is a fact about the rendered box, so it is MEASURED, not
    // guessed from the string length: the same address fits on a desktop and
    // is cut on a phone, and the panel is a column that resizes.
    const measure = () => setTruncated(input.scrollWidth > input.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(input);
    return () => observer.disconnect();
  }, [props.value]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(props.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="my-profile-copy-field">
      <label htmlFor={fieldId}>{props.label}</label>
      <span className="my-profile-copy-row">
        <input
          id={fieldId}
          onChange={(event) => props.onChange(event.target.value)}
          placeholder={props.placeholder}
          ref={inputRef}
          required={props.required}
          type={props.type}
          value={props.value}
        />
        {props.value && (
          <button
            aria-label={props.t("staffConsole.myProfile.copyValue")}
            className="my-profile-copy-button"
            onClick={() => void copy()}
            title={props.t(copied ? "staffConsole.myProfile.copiedValue" : "staffConsole.myProfile.copyValue")}
            type="button"
          >
            {copied ? (
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m5 13 4 4 10-10" /></svg>
            ) : (
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
                <rect height="12" rx="2" width="10" x="9" y="9" />
                <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
        )}
      </span>
      {truncated && props.value && (
        <p className="my-profile-long-value">
          <code aria-label={`${props.label}: ${props.value}`}>{props.value}</code>
        </p>
      )}
      {copied && <span className="visually-hidden" role="status">{props.t("staffConsole.myProfile.copiedValue")}</span>}
    </div>
  );
}

// ---- Human-readable panes (Pavel 2026-07-21: no raw JSON in partner view) ----

/**
 * F138b: the answers surface itself lives in `src/staff/AnswersView.tsx` (its
 * own file and its own stylesheet, so the parallel F138 workers never meet in
 * this module). What stays here is the thin wrapper every call site already
 * imports, and the sort rank the console re-exports.
 *
 * The three hand-typed order arrays that used to sit here are GONE: they had
 * drifted from the questionnaire they claimed to mirror, so seven live q1/q2
 * questions sorted to the bottom in a lump and two retired ones were still
 * listed. The order and the chapters now come from `shared/answer-catalog.ts`,
 * which `tests/vystupy-f138.test.ts` holds against the real catalog.
 */
export function staffAnswerSortRank(row: Record<string, unknown>): number {
  return answerSortRank(row);
}

export function AnswersPane(props: StaffPaneProps & { questionnaire?: "q1" | "q2" }) {
  return <AnswersView {...props} />;
}

// Machine briefs generated from the questionnaires (q1 feeds the Instance
// Creator, q2 the DFY builder). The partner reads status and key facts in
// Czech; the raw contract JSON stays admin-only.
export function SubmissionsPane(props: StaffPaneProps & { questionnaire?: "q1" | "q2" }) {
  const rows = props.questionnaire
    ? props.detail.submissions.filter((row) => String(row.questionnaire) === props.questionnaire)
    : props.detail.submissions;
  if (!rows.length) return <p className="staff-empty">{props.t("staffConsole.emptySubmissions")}</p>;
  const safe = makeSafeT(props.t);
  return (
    <div className="staff-card-list">
      {rows.map((row) => {
        const payload = (row.icPayload && typeof row.icPayload === "object" ? row.icPayload : null) as Record<string, unknown> | null;
        const client = (payload?.client && typeof payload.client === "object" ? payload.client : null) as Record<string, unknown> | null;
        const name = (payload?.name && typeof payload.name === "object" ? payload.name : null) as Record<string, unknown> | null;
        const status = String(row.status);
        return (
          <article className="staff-card" key={String(row.id)}>
            <div className="submission-head">
              <h3>{props.t(`sections.${String(row.questionnaire)}.title`)}</h3>
              <span className={`submission-status is-${status}`}>{safe(`staffConsole.submissionState.${status}`, status.replaceAll("_", " "))}</span>
            </div>
            <dl className="staff-definition">
              {typeof row.submittedAt === "string" && <div><dt>{props.t("staffConsole.submittedAt")}</dt><dd>{formatDate(String(row.submittedAt), props.locale)}</dd></div>}
              {typeof row.exportedAt === "string" && <div><dt>{props.t("staffConsole.submissionExportedAt")}</dt><dd>{formatDate(String(row.exportedAt), props.locale)}</dd></div>}
              {typeof name?.buddy_name === "string" && (
                <div><dt>{props.t("staffConsole.submissionBuddy")}</dt><dd>{name.buddy_name}{typeof name.telegram_username === "string" && name.telegram_username ? ` (@${name.telegram_username})` : ""}</dd></div>
              )}
              {typeof client?.market_language === "string" && (
                <div><dt>{props.t("staffConsole.submissionLanguage")}</dt><dd>{safe(`staffConsole.submissionLanguageName.${client.market_language}`, String(client.market_language))}</dd></div>
              )}
              {typeof client?.instance_start_mode === "string" && (
                <div><dt>{props.t("staffConsole.submissionInstance")}</dt><dd>{safe(`staffConsole.submissionInstanceMode.${client.instance_start_mode}`, String(client.instance_start_mode))}</dd></div>
              )}
            </dl>
            {props.role === "admin" && (
              <details className="raw-details"><summary>{props.t("staffConsole.rawData")}</summary><pre>{JSON.stringify(row, null, 2)}</pre></details>
            )}
          </article>
        );
      })}
    </div>
  );
}

// The client's mind map, read-only for staff: facts grouped into branches.
export function ClientMapPane(props: StaffPaneProps) {
  const facts = props.detail.profileFacts ?? [];
  if (!facts.length) return <p className="staff-empty">{props.t("staffConsole.emptyProfile")}</p>;
  const branches = new Map<string, typeof facts>();
  for (const fact of facts) {
    const list = branches.get(fact.category) ?? [];
    list.push(fact);
    branches.set(fact.category, list);
  }
  return (
    <div className="client-map">
      <p className="client-map-center">{props.detail.client.name}</p>
      <div className="client-map-branches">
        {[...branches.entries()].map(([category, items]) => (
          <article className="staff-card client-map-branch" key={category}>
            <h3>{makeSafeT(props.t)(`questionnaire.factCategory.${category}`, category)}</h3>
            <ul>{items.map((fact) => <li key={fact.id}>{fact.text}</li>)}</ul>
          </article>
        ))}
      </div>
    </div>
  );
}

export function AuditPane(props: StaffPaneProps) {
  const rows = props.detail.audit.map((row) => ({
    id: String(row.id),
    type: String(row.type),
    createdAt: String(row.createdAt),
    data: (row.data && typeof row.data === "object" ? row.data : null) as Record<string, unknown> | null,
    actorId: typeof row.actorId === "string" ? row.actorId : null,
    actorName: typeof row.actorName === "string" ? row.actorName : null
  }));
  return <ActivityTimeline rows={rows} locale={props.locale} t={props.t} isAdmin={props.role === "admin"} />;
}
